import React, { Fragment, useState, useEffect } from 'react';
import { masterListApi, clientDocumentsApi, externalDocumentsApi, sectorsApi, typesApi, useAuth } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import SectorFilter, { useSectorFilter } from '../components/SectorFilter';
import { toast } from 'sonner';
import { formatDateBR } from '../utils/dateValidation';
import {
  ListChecks,
  Search,
  Edit,
  X,
  Loader2,
  FileText,
  Calendar,
  AlertTriangle,
  FolderOpen,
  Users,
  Download,
  Paperclip,
  Building2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'badge-gray',
  pending_review: 'badge-yellow',
  pending_approval: 'badge-blue',
  approved: 'badge-green',
  obsolete: 'badge-red',
  cancelled: 'badge-gray',
};

const STATUS_LABELS = {
  draft: 'Rascunho',
  pending_review: 'Aguardando Revisão',
  pending_approval: 'Aguardando Aprovação',
  approved: 'Aprovado',
  obsolete: 'Obsoleto',
  cancelled: 'Cancelado',
};

const TABS = [
  { id: 'gestao', label: 'Gestão de Documentos', icon: FileText },
  { id: 'clientes', label: 'Documentos dos Clientes', icon: Users },
  { id: 'externos', label: 'Documentos Externos', icon: FolderOpen },
];

const CONTROL_STORAGE_LABELS = {
  year: 'Por Ano',
  certificate: 'Por Certificado',
};

const CONTROL_RETENTION_LABELS = {
  annual: 'Anual',
  biennial: 'Bienal',
};

const CONTROL_DISPOSAL_LABELS = {
  annual: 'Anual',
  monthly: 'Mensal',
  semiannual: 'Semestral',
  quarterly: 'Trimestral',
  valid: 'Vigente',
};

// Padrões de preenchimento da Lista Mestra (conforme a planilha oficial)
const ML_DEFAULTS = {
  reference_standard: 'ISO',
  document_format: 'Documento em texto',
  recovery_info: 'Título',
  retention_period: 'Última Revisão',
  disposition: 'Picotar e Reciclar',
};
const ML_SUGGESTIONS = {
  reference_standard: ['ISO', 'ISO 9001:2015', 'ISO 13485', 'ISO 14001', 'ISO 45001'],
  document_format: ['Documento em texto', 'Planilha', 'Formulário', 'PDF', 'Imagem'],
  recovery_info: ['Título', 'Código', 'Setor'],
  retention_period: ['Última Revisão', '1 ano', '2 anos', '5 anos', 'Permanente'],
  disposition: ['Picotar e Reciclar', 'Arquivo morto', 'Destruição', 'Digitalização'],
};

const MasterList = () => {
  const { user, loading: authLoading } = useAuth();
  const { sectorFilter, setSectorFilter, effectiveFilter } = useSectorFilter(user);

  const [activeTab, setActiveTab] = useState('gestao');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sectors, setSectors] = useState([]);
  const [types, setTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    copy_number: 1,
    distribution_list: '',
    retention_period: '',
    storage_location: '',
    origin_type: 'interno',
    storage_medium: 'digital',
    access_level: 'irrestrito',
    recovery_info: '',
    disposition: '',
    reference_standard: '',
    scope: '',
    document_format: '',
  });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  const [clientDocs, setClientDocs] = useState([]);
  const [clientStats, setClientStats] = useState(null);
  const [clientGroups, setClientGroups] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientLoaded, setClientLoaded] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  const [externalDocs, setExternalDocs] = useState([]);
  const [externalStats, setExternalStats] = useState(null);
  const [externalGroups, setExternalGroups] = useState([]);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalLoaded, setExternalLoaded] = useState(false);
  const [externalSearch, setExternalSearch] = useState('');

  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    // Wait for AuthProvider to resolve the user from localStorage first — otherwise
    // effectiveFilter starts as '' and flips to the user's sector a tick later,
    // firing this effect a second time with the correct filter.
    if (authLoading) return;
    loadData();
    loadSectors();
    loadTypes();
  }, [authLoading, effectiveFilter, filterType, filterStatus]);

  useEffect(() => {
    if (activeTab === 'clientes' && !clientLoaded) {
      loadClientData();
    }
  }, [activeTab, clientLoaded]);

  useEffect(() => {
    if (activeTab === 'externos' && !externalLoaded) {
      loadExternalData();
    }
  }, [activeTab, externalLoaded]);

  const loadTypes = async () => {
    try {
      const response = await typesApi.listActive();
      setTypes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
    }
  };

  const loadSectors = async () => {
    try {
      const response = await sectorsApi.listActive();
      setSectors(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (effectiveFilter) params.sector = effectiveFilter;
      if (filterType) params.document_type = filterType;
      if (filterStatus) params.document_status = filterStatus;

      const [itemsRes, statsRes] = await Promise.all([
        masterListApi.list(params),
        masterListApi.stats(),
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar lista mestra');
    } finally {
      setLoading(false);
    }
  };

  const loadClientData = async () => {
    try {
      setClientLoading(true);
      const [docsRes, statsRes, groupsRes] = await Promise.all([
        clientDocumentsApi.list(),
        clientDocumentsApi.stats(),
        clientDocumentsApi.listGroups(),
      ]);
      setClientDocs(docsRes.data || []);
      setClientStats(statsRes.data);
      setClientGroups(groupsRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar documentos dos clientes');
    } finally {
      setClientLoading(false);
      setClientLoaded(true);
    }
  };

  const loadExternalData = async () => {
    try {
      setExternalLoading(true);
      const [docsRes, statsRes, groupsRes] = await Promise.all([
        externalDocumentsApi.list(),
        externalDocumentsApi.stats(),
        externalDocumentsApi.listGroups(),
      ]);
      setExternalDocs(docsRes.data || []);
      setExternalStats(statsRes.data);
      setExternalGroups(groupsRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar documentos externos');
    } finally {
      setExternalLoading(false);
      setExternalLoaded(true);
    }
  };

  const handleDownloadClientFile = async (doc) => {
    if (!doc.has_file) return;
    setDownloadingId(doc.id);
    try {
      const res = await clientDocumentsApi.downloadFile(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || `documento_${doc.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadExternalFile = async (doc) => {
    if (!doc.has_file) return;
    setDownloadingId(doc.id);
    try {
      const res = await externalDocumentsApi.downloadFile(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || `documento_${doc.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpenModal = (item) => {
    setEditingItem(item);
    setFormData({
      copy_number: item.copy_number || 1,
      distribution_list: item.distribution_list || '',
      retention_period: item.retention_period || ML_DEFAULTS.retention_period,
      storage_location: item.storage_location || '',
      origin_type: item.origin_type || 'interno',
      storage_medium: item.storage_medium || 'digital',
      access_level: item.access_level || 'irrestrito',
      recovery_info: item.recovery_info || ML_DEFAULTS.recovery_info,
      disposition: item.disposition || ML_DEFAULTS.disposition,
      reference_standard: item.reference_standard || ML_DEFAULTS.reference_standard,
      scope: item.scope || '',
      document_format: item.document_format || ML_DEFAULTS.document_format,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await masterListApi.update(editingItem.id, formData);
      toast.success('Item atualizado com sucesso!');
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar item');
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = items.filter(item =>
    (item.document_code?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.document_title?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Check if review date is approaching (within 30 days)
  const isReviewApproaching = (date) => {
    if (!date) return false;
    const reviewDate = new Date(date);
    const today = new Date();
    const diffDays = Math.ceil((reviewDate - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  };

  // Check if review date has passed
  const isReviewOverdue = (date) => {
    if (!date) return false;
    const reviewDate = new Date(date);
    const today = new Date();
    return reviewDate < today;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const COLUMNS = [
    { key: 'codigo', label: 'Código', locked: true, render: (item) => (
      <td className="font-medium text-white">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-green-500" />
          {item.document_code}
        </div>
      </td>
    ) },
    { key: 'titulo', label: 'Título', render: (item) => <td className="max-w-xs truncate" title={item.document_title}>{item.document_title}</td> },
    { key: 'norma', label: 'Norma de Referência', render: (item) => <td className="max-w-[140px] truncate" title={item.reference_standard || ''}>{item.reference_standard || '-'}</td> },
    { key: 'tipo', label: 'Tipo', render: (item) => <td>{item.document_type || '-'}</td> },
    { key: 'setor', label: 'Setor', render: (item) => <td>{item.sector || '-'}</td> },
    { key: 'revisao', label: 'Rev.', render: (item) => <td className="text-center">{item.current_revision}</td> },
    { key: 'status', label: 'Status', render: (item) => (
      <td>
        <span className={`badge ${STATUS_COLORS[item.document_status] || 'badge-gray'}`}>
          {STATUS_LABELS[item.document_status] || item.document_status}
        </span>
      </td>
    ) },
    { key: 'aprovacao', label: 'Aprovação', render: (item) => (
      <td>
        <div className="flex items-center gap-1">
          <Calendar size={14} className="text-gray-500" />
          {formatDate(item.approval_date)}
        </div>
      </td>
    ) },
    { key: 'proxima_revisao', label: 'Próx. Revisão', render: (item) => {
      const reviewOverdue = isReviewOverdue(item.next_review_date);
      const reviewApproaching = isReviewApproaching(item.next_review_date);
      return (
        <td>
          <div className="flex items-center gap-1">
            {reviewOverdue && <AlertTriangle size={14} className="text-red-400" title="Revisão vencida" />}
            {reviewApproaching && !reviewOverdue && <AlertTriangle size={14} className="text-yellow-400" title="Revisão próxima" />}
            <span className={reviewOverdue ? 'text-red-400' : reviewApproaching ? 'text-yellow-400' : ''}>
              {formatDate(item.next_review_date)}
            </span>
          </div>
        </td>
      );
    } },
    { key: 'copias', label: 'Cópias', render: (item) => <td className="text-center">{item.copy_number}</td> },
    { key: 'origem', label: 'Origem', render: (item) => (
      <td className="text-center">
        <span className={`badge text-xs ${item.origin_type === 'externo' ? 'badge-blue' : 'badge-gray'}`}>
          {item.origin_type === 'externo' ? 'Externo' : 'Interno'}
        </span>
      </td>
    ) },
    { key: 'abrangencia', label: 'Abrangência/Aplicabilidade', render: (item) => <td className="max-w-[160px] truncate" title={item.scope || ''}>{item.scope || '-'}</td> },
    { key: 'formato', label: 'Formato', render: (item) => <td>{item.document_format || '-'}</td> },
    { key: 'meio', label: 'Meio', render: (item) => (
      <td className="text-center">
        <span className={`badge text-xs ${item.storage_medium === 'físico' ? 'badge-yellow' : 'badge-gray'}`}>
          {item.storage_medium === 'físico' ? 'Físico' : item.storage_medium === 'ambos' ? 'Digital/Físico' : 'Digital'}
        </span>
      </td>
    ) },
    { key: 'acesso', label: 'Acesso', render: (item) => (
      <td className="text-center">
        <span className={`badge text-xs ${item.access_level === 'restrito' ? 'badge-red' : 'badge-gray'}`}>
          {item.access_level === 'restrito' ? 'Restrito' : 'Irrestrito (livre acesso)'}
        </span>
      </td>
    ) },
    { key: 'local', label: 'Local', render: (item) => <td className="max-w-[100px] truncate" title={item.storage_location}>{item.storage_location || '-'}</td> },
    { key: 'retencao', label: 'Retenção', render: (item) => <td className="text-sm text-gray-400">{item.retention_period || '-'}</td> },
    { key: 'recuperacao', label: 'Recuperação', render: (item) => <td className="max-w-[100px] truncate text-sm text-gray-400" title={item.recovery_info || ''}>{item.recovery_info || '-'}</td> },
    { key: 'disposicao', label: 'Disposição', render: (item) => <td className="max-w-[100px] truncate text-sm text-gray-400" title={item.disposition || ''}>{item.disposition || '-'}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (item) => (
      <td>
        <div className="flex items-center justify-end">
          <button
            onClick={() => handleOpenModal(item)}
            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
            title="Editar"
            data-testid="edit-btn"
          >
            <Edit size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'master-list', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="master-list-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <ListChecks className="text-green-500" />
            Lista Mestra
          </h1>
          <p className="text-gray-400 mt-1">Registro central de documentos controlados (internos, clientes e externos)</p>
        </div>
        {activeTab === 'gestao' && (
          <div className="flex items-center gap-2">
            <ExportButtons 
              moduleName="master-list" 
              filters={{ sector: sectorFilter, document_type: filterType, document_status: filterStatus }}
            />
            <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-green-400 border-green-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Gestão de Documentos */}
      {activeTab === 'gestao' && (
        <>
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Total</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Aprovados</p>
                <p className="text-2xl font-bold text-green-400">{stats.by_status?.approved || 0}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Em Revisão</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {(stats.by_status?.pending_review || 0) + (stats.by_status?.draft || 0)}
                </p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Obsoletos</p>
                <p className="text-2xl font-bold text-red-400">{stats.by_status?.obsolete || 0}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Setores</p>
                <p className="text-2xl font-bold text-blue-400">{Object.keys(stats.by_sector || {}).length}</p>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por código ou título..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
                data-testid="search-input"
              />
            </div>
            <SectorFilter
              user={user}
              value={sectorFilter}
              onChange={(val) => { setSectorFilter(val); }}
              sectors={sectors}
              className="input px-3 w-full sm:w-60"
            />
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); }}
              className="input px-3 w-full sm:w-60"
              data-testid="type-filter"
            >
              <option value="">Todos os Tipos</option>
              {types.map(type => (
                <option key={type.id} value={type.name}>{type.name}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); }}
              className="input px-3 w-full sm:w-60"
              data-testid="status-filter"
            >
              <option value="">Todos os Status</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Table */}
          <div className="card">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-green-500" size={32} />
              </div>
            ) : (
              <div className="table-container">
                <table className="table min-w-max">
                  <thead>
                    <tr>
                      {visibleColumns.map(col => (
                        <th key={col.key} className={col.headerClass || ''} title={col.label}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td colSpan={visibleColumns.length} className="text-center py-8 text-gray-500">
                          Nenhum documento na lista mestra
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map(item => {
                        const reviewOverdue = isReviewOverdue(item.next_review_date);
                        const reviewApproaching = isReviewApproaching(item.next_review_date);

                        return (
                          <tr key={item.id} className={reviewOverdue ? 'bg-red-500/10' : reviewApproaching ? 'bg-yellow-500/10' : ''}>
                            {visibleColumns.map(col => (
                              <Fragment key={col.key}>{col.render(item)}</Fragment>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500/20 rounded"></div>
              <span>Revisão vencida</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500/20 rounded"></div>
              <span>Revisão em até 30 dias</span>
            </div>
          </div>
        </>
      )}

      {/* Documentos dos Clientes */}
      {activeTab === 'clientes' && (
        <>
          {clientStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Total de Documentos</p>
                <p className="text-2xl font-bold text-white">{clientStats.total}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Vencidos</p>
                <p className="text-2xl font-bold text-red-400">{clientStats.expired}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Vencem neste mês</p>
                <p className="text-2xl font-bold text-yellow-400">{clientStats.due_soon}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Grupos</p>
                <p className="text-2xl font-bold text-white">{clientGroups.length}</p>
              </div>
            </div>
          )}
          <ClientDocumentsByGroupTable
            documents={clientDocs}
            groups={clientGroups}
            loading={clientLoading}
            searchTerm={clientSearch}
            onSearchChange={setClientSearch}
            onDownload={handleDownloadClientFile}
            downloadingId={downloadingId}
            emptyMessage="Nenhum documento de cliente encontrado"
          />
        </>
      )}

      {/* Documentos Externos */}
      {activeTab === 'externos' && (
        <>
          {externalStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Total de Documentos</p>
                <p className="text-2xl font-bold text-white">{externalStats.total}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Vencidos</p>
                <p className="text-2xl font-bold text-red-400">{externalStats.expired}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Vencem neste mês</p>
                <p className="text-2xl font-bold text-yellow-400">{externalStats.due_soon}</p>
              </div>
              <div className="card p-4">
                <p className="text-gray-400 text-sm">Grupos</p>
                <p className="text-2xl font-bold text-white">{externalGroups.length}</p>
              </div>
            </div>
          )}
          <ControlDocumentsTable
            documents={externalDocs}
            groups={externalGroups}
            loading={externalLoading}
            searchTerm={externalSearch}
            onSearchChange={setExternalSearch}
            onDownload={handleDownloadExternalFile}
            downloadingId={downloadingId}
            emptyMessage="Nenhum documento externo encontrado"
          />
        </>
      )}

      {/* Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" onClick={handleCloseModal} />
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Editar Informações</h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <p className="text-gray-400 mb-4">
              Documento: <span className="text-white font-medium">{editingItem?.document_code} - {editingItem?.document_title}</span>
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Número de Cópias</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.copy_number}
                    onChange={(e) => setFormData({ ...formData, copy_number: parseInt(e.target.value) || 1 })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Local de Armazenamento</label>
                  <input
                    type="text"
                    value={formData.storage_location}
                    list="ml-sectors"
                    onChange={(e) => setFormData({ ...formData, storage_location: e.target.value })}
                    className="input"
                    placeholder="Ex: Pasta Física - Armário 1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Origem</label>
                  <select
                    value={formData.origin_type}
                    onChange={(e) => setFormData({ ...formData, origin_type: e.target.value })}
                    className="input"
                  >
                    <option value="interno">Interno</option>
                    <option value="externo">Externo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Meio</label>
                  <select
                    value={formData.storage_medium}
                    onChange={(e) => setFormData({ ...formData, storage_medium: e.target.value })}
                    className="input"
                  >
                    <option value="digital">Digital</option>
                    <option value="físico">Físico</option>
                    <option value="ambos">Digital/Físico (Qualidade)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Acesso</label>
                  <select
                    value={formData.access_level}
                    onChange={(e) => setFormData({ ...formData, access_level: e.target.value })}
                    className="input"
                  >
                    <option value="irrestrito">Irrestrito (livre acesso)</option>
                    <option value="restrito">Restrito</option>
                  </select>
                </div>
              </div>

              {Object.entries(ML_SUGGESTIONS).map(([k, opts]) => (
                <datalist key={k} id={`ml-${k}`}>{opts.map(o => <option key={o} value={o} />)}</datalist>
              ))}
              <datalist id="ml-sectors">
                {sectors.map(sc => <option key={sc.id || sc.name} value={sc.name} />)}
              </datalist>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Norma de Referência</label>
                  <input
                    type="text"
                    value={formData.reference_standard}
                    list="ml-reference_standard"
                    onChange={(e) => setFormData({ ...formData, reference_standard: e.target.value })}
                    className="input"
                    placeholder="Ex: ISO 9001:2015 7.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Abrangência/Aplicabilidade</label>
                  <input
                    type="text"
                    value={formData.scope}
                    list="ml-sectors"
                    onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                    className="input"
                    placeholder="Ex: Todos os setores / Qualidade"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Formato</label>
                  <input
                    type="text"
                    value={formData.document_format}
                    list="ml-document_format"
                    onChange={(e) => setFormData({ ...formData, document_format: e.target.value })}
                    className="input"
                    placeholder="Ex: PDF, Word, Excel"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tempo de Retenção</label>
                <input
                  type="text"
                  value={formData.retention_period}
                    list="ml-retention_period"
                  onChange={(e) => setFormData({ ...formData, retention_period: e.target.value })}
                  className="input"
                  placeholder="Ex: 5 anos"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Recuperação</label>
                  <input
                    type="text"
                    value={formData.recovery_info}
                    list="ml-recovery_info"
                    onChange={(e) => setFormData({ ...formData, recovery_info: e.target.value })}
                    className="input"
                    placeholder="Ex: Pasta eletrónica / Arquivo físico armário 2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Disposição</label>
                  <input
                    type="text"
                    value={formData.disposition}
                    list="ml-disposition"
                    onChange={(e) => setFormData({ ...formData, disposition: e.target.value })}
                    className="input"
                    placeholder="Ex: Destruição / Arquivo morto / Digitalização"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Lista de Distribuição</label>
                <textarea
                  value={formData.distribution_list}
                  onChange={(e) => setFormData({ ...formData, distribution_list: e.target.value })}
                  className="input min-h-[80px]"
                  placeholder="Lista de pessoas/setores que recebem cópia controlada"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

const ClientDocumentsByGroupTable = ({
  documents,
  groups,
  loading,
  searchTerm,
  onSearchChange,
  onDownload,
  downloadingId,
  emptyMessage,
}) => {
  const groupName = (id) => groups.find((g) => g.id === id)?.name || '-';

  const filtered = searchTerm
    ? documents.filter((d) =>
        [d.name, d.edition, d.area_name, groupName(d.group_id)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : documents;

  const knownGroupIds = new Set(groups.map((g) => g.id));
  const grouped = groups
    .map((group) => ({
      group,
      docs: filtered.filter((d) => d.group_id === group.id),
    }))
    .filter(({ docs }) => docs.length > 0);
  const ungrouped = filtered.filter((d) => !knownGroupIds.has(d.group_id));

  const renderRows = (docs) =>
    docs.map((doc) => (
      <tr key={doc.id}>
        <td className="text-white font-medium">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-green-500 flex-shrink-0" />
            <span
              title={
                doc.access_area_names?.length > 0
                  ? `Áreas com acesso: ${doc.access_area_names.join(', ')}`
                  : 'Acesso: todos os setores'
              }
            >
              {doc.name}
            </span>
          </div>
        </td>
        <td className="text-gray-400">{doc.edition || '-'}</td>
        <td className="text-gray-400">
          {doc.area_name ? (
            <span className="flex items-center gap-1">
              <Building2 size={13} className="text-gray-500" />
              {doc.area_name}
            </span>
          ) : '-'}
        </td>
        <td className="text-gray-400">{CONTROL_STORAGE_LABELS[doc.storage_type] || doc.storage_type || '-'}</td>
        <td className="text-gray-400">{CONTROL_RETENTION_LABELS[doc.retention_min] || doc.retention_min || '-'}</td>
        <td className="text-gray-400">{CONTROL_DISPOSAL_LABELS[doc.disposal] || doc.disposal || '-'}</td>
        <td className="text-gray-400">{formatDateBR(doc.expiration_date)}</td>
        <td>
          {doc.days_to_expire == null ? (
            <span className="text-gray-500">-</span>
          ) : doc.days_to_expire < 0 ? (
            <span className="text-red-400 font-medium">
              Vencido há {Math.abs(doc.days_to_expire)} {Math.abs(doc.days_to_expire) === 1 ? 'dia' : 'dias'}
            </span>
          ) : doc.days_to_expire <= 30 ? (
            <span className="text-yellow-400 font-medium">
              {doc.days_to_expire} {doc.days_to_expire === 1 ? 'dia' : 'dias'}
            </span>
          ) : (
            <span className="text-green-400 font-medium">
              {doc.days_to_expire} {doc.days_to_expire === 1 ? 'dia' : 'dias'}
            </span>
          )}
        </td>
        <td>
          {doc.is_expired ? (
            <span className="badge badge-red flex items-center gap-1 w-fit">
              <AlertCircle size={12} />
              Vencido
            </span>
          ) : (
            <span className="badge badge-green flex items-center gap-1 w-fit">
              <CheckCircle size={12} />
              Vigente
            </span>
          )}
        </td>
        <td>
          {doc.has_file ? (
            <button
              onClick={() => onDownload(doc)}
              className="text-blue-400 hover:text-blue-300"
              title="Baixar anexo"
            >
              {downloadingId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            </button>
          ) : (
            <span className="text-gray-600" title="Sem anexo">
              <Paperclip size={14} />
            </span>
          )}
        </td>
      </tr>
    ));

  const tableHead = (
    <thead>
      <tr>
        <th>Nome</th>
        <th>Edição</th>
        <th>Área</th>
        <th>Armazenamento</th>
        <th>Retenção</th>
        <th>Disposição</th>
        <th>Vencimento</th>
        <th>Dias p/ Vencer</th>
        <th>Situação</th>
        <th>Anexo</th>
      </tr>
    </thead>
  );

  return (
    <div className="card overflow-hidden">
      <div className="p-3 border-b border-gray-700">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar nome, edição, área ou cliente..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
      </div>
      {loading ? (
        <div className="table-container">
          <div className="py-12 text-center">
            <Loader2 className="animate-spin text-green-500 inline" size={28} />
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="table-container">
          <div className="py-12 text-center text-gray-500">{emptyMessage}</div>
        </div>
      ) : (
        <div className="space-y-6 p-4">
          {grouped.map(({ group, docs }) => (
            <div key={group.id} className="overflow-hidden rounded-lg border border-gray-700">
              <div className="flex items-center justify-between gap-3 bg-gray-800/60 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Users size={16} className="text-green-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{group.name}</p>
                    {group.description && (
                      <p className="text-xs text-gray-500 truncate">{group.description}</p>
                    )}
                  </div>
                </div>
                <span className="badge badge-gray flex-shrink-0">
                  {docs.length} {docs.length === 1 ? 'documento' : 'documentos'}
                </span>
              </div>
              <div className="table-container">
                <table className="table">
                  {tableHead}
                  <tbody>{renderRows(docs)}</tbody>
                </table>
              </div>
            </div>
          ))}
          {ungrouped.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-gray-700">
              <div className="flex items-center justify-between gap-3 bg-gray-800/60 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Users size={16} className="text-gray-500 flex-shrink-0" />
                  <p className="text-sm font-semibold text-white truncate">Sem grupo</p>
                </div>
                <span className="badge badge-gray flex-shrink-0">
                  {ungrouped.length} {ungrouped.length === 1 ? 'documento' : 'documentos'}
                </span>
              </div>
              <div className="table-container">
                <table className="table">
                  {tableHead}
                  <tbody>{renderRows(ungrouped)}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ControlDocumentsTable = ({
  documents,
  groups,
  loading,
  searchTerm,
  onSearchChange,
  onDownload,
  downloadingId,
  emptyMessage,
}) => {
  const groupName = (id) => groups.find((g) => g.id === id)?.name || '-';

  const filtered = searchTerm
    ? documents.filter((d) =>
        [d.name, d.edition, d.area_name, groupName(d.group_id)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : documents;

  return (
    <div className="card overflow-hidden">
      <div className="p-3 border-b border-gray-700">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar nome, edição, área ou grupo..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
      </div>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Grupo</th>
              <th>Edição</th>
              <th>Área</th>
              <th>Armazenamento</th>
              <th>Retenção</th>
              <th>Disposição</th>
              <th>Vencimento</th>
              <th>Dias p/ Vencer</th>
              <th>Situação</th>
              <th>Anexo</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="py-12 text-center">
                  <Loader2 className="animate-spin text-green-500 inline" size={28} />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="11" className="py-12 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              filtered.map((doc) => (
                <tr key={doc.id}>
                  <td className="text-white font-medium">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-green-500 flex-shrink-0" />
                      <span
                        title={
                          doc.access_area_names?.length > 0
                            ? `Áreas com acesso: ${doc.access_area_names.join(', ')}`
                            : 'Acesso: todos os setores'
                        }
                      >
                        {doc.name}
                      </span>
                    </div>
                  </td>
                  <td className="text-gray-400">{groupName(doc.group_id)}</td>
                  <td className="text-gray-400">{doc.edition || '-'}</td>
                  <td className="text-gray-400">
                    {doc.area_name ? (
                      <span className="flex items-center gap-1">
                        <Building2 size={13} className="text-gray-500" />
                        {doc.area_name}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="text-gray-400">{CONTROL_STORAGE_LABELS[doc.storage_type] || doc.storage_type || '-'}</td>
                  <td className="text-gray-400">{CONTROL_RETENTION_LABELS[doc.retention_min] || doc.retention_min || '-'}</td>
                  <td className="text-gray-400">{CONTROL_DISPOSAL_LABELS[doc.disposal] || doc.disposal || '-'}</td>
                  <td className="text-gray-400">{formatDateBR(doc.expiration_date)}</td>
                  <td>
                    {doc.days_to_expire == null ? (
                      <span className="text-gray-500">-</span>
                    ) : doc.days_to_expire < 0 ? (
                      <span className="text-red-400 font-medium">
                        Vencido há {Math.abs(doc.days_to_expire)} {Math.abs(doc.days_to_expire) === 1 ? 'dia' : 'dias'}
                      </span>
                    ) : doc.days_to_expire <= 30 ? (
                      <span className="text-yellow-400 font-medium">
                        {doc.days_to_expire} {doc.days_to_expire === 1 ? 'dia' : 'dias'}
                      </span>
                    ) : (
                      <span className="text-green-400 font-medium">
                        {doc.days_to_expire} {doc.days_to_expire === 1 ? 'dia' : 'dias'}
                      </span>
                    )}
                  </td>
                  <td>
                    {doc.is_expired ? (
                      <span className="badge badge-red flex items-center gap-1 w-fit">
                        <AlertCircle size={12} />
                        Vencido
                      </span>
                    ) : (
                      <span className="badge badge-green flex items-center gap-1 w-fit">
                        <CheckCircle size={12} />
                        Vigente
                      </span>
                    )}
                  </td>
                  <td>
                    {doc.has_file ? (
                      <button
                        onClick={() => onDownload(doc)}
                        className="text-blue-400 hover:text-blue-300"
                        title="Baixar anexo"
                      >
                        {downloadingId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      </button>
                    ) : (
                      <span className="text-gray-600" title="Sem anexo">
                        <Paperclip size={14} />
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MasterList;